/**
 * Frame orchestration over the DESIGN 6.2 layers: (1) pre-rendered ground,
 * (2) floor hazards + ground effects + aim ghost, (3) corpses, (4) bodies
 * sorted by y (players, enemies, projectiles, pillars and wall / barrier
 * chords, each drawn upright with its drop shadow on the ground), (5) labels,
 * (6) beams / sprays / arcs with additive blending, (7) particles, (8)
 * floating numbers, (9) canvas HUD. DOM overlays (10) belong to WP7.
 *
 * The canvas is sized to the window at the device pixel ratio with one
 * setTransform per frame; world drawing happens in CSS px through the
 * Camera (32 px per unit). The camera follows the local player with 25%
 * cursor lookahead; a dead local player gets a free camera (edge pan with
 * the cursor, arrow keys to pan). `setReduceMotion` disables screen shake.
 *
 * Deviation from the literal layer list: wall / barrier chords are folded
 * into the y-sort instead of a separate layer after the bodies, so a wizard
 * standing in front of a wall is never covered by its front face.
 */

import { ARENA, PLAYER_SPEED, levelById, type Arena, type Vec2 } from '@shared/index.js';
import type { ViewModel, ViewPlayer } from '../state/viewModel.js';
import { ENEMY_COLORS, GROUND } from '../palette.js';
import { type Ctx } from './gfx.js';
import { Camera, FREE_PAN_SPEED } from './camera.js';
import { FEEL, deathPose, easeK, spawnPose, type Pose } from './feel.js';
import { ART_MANIFEST_URL, art } from './sprites.js';
import { EARLY_ART, FX_ART, LATE_ART_FROM_WAVE, enemySetName, levelArt } from './spriteSets.js';
import { drawStormColumn, FX_SHOTS_MAX, resetHazards, setSprayBudget, sprayPuffs, stormColumns, sweepFrozen, sweepStormColumns, trackHazards, type HazardSeen } from './spellArt.js';
import { Ground, trailGlyphHint } from './ground.js';
import { Particles } from './particles.js';
import { Numbers } from './numbers.js';
import { Effects } from './effects.js';
import { Hud } from './hud.js';
import { dominantOf, drawCorpse, drawPillar, drawProjectile, drawProjectileShadow, drawWizard, drawWizardGround, drawWizardLabels, projectileHeight } from './entities.js';
import { drawEnemy, drawEnemyGround, drawEnemyLabels } from './enemies.js';
import { emitStatusParticles, emptyLook, statusLook } from './statusFx.js';
import { drawArc, drawBeam, drawHazardFloor, drawHazardHpHint, drawHazardSegment, drawSpray, emitBeamSparks, hazardSegments, sweepHazardCache } from './spells.js';
import { drawAimGhost, drawCursor, drawThrowPreview } from './aimGhost.js';
import { drawCarriedFlag, drawGroundFlags, drawTeamRing } from './pvp.js';
import { DOOR_SINK_S, drawBrambles, drawChasmFills, drawDoor, drawFlood, drawIce, drawRuneStone, stoneActive } from './story.js';
import { drawBossRage, drawMarkerBody, drawMarkerFloor, drawMarkerSky, markerStandsUp } from './markers.js';
import { Weather } from './weather.js';
import { drawDepthMarks, drawTimeStop } from './depthFx.js';

const ITEM_PLAYER = 0, ITEM_ENEMY = 1, ITEM_PROJ = 2, ITEM_PILLAR = 3, ITEM_SEG = 4, ITEM_MARKER = 5, ITEM_DOOR = 6, ITEM_STONE = 7, ITEM_STORM = 8;
/** Phones keep a smaller share of particles and one-shot sprites (the frame budget is tighter). */
const TOUCH_PARTICLE_BUDGET = 0.5;
const TOUCH_SHOTS = 40;
interface DrawItem { y: number; kind: number; i: number; j: number }

/** Cursor this close to a screen edge pans the free camera (spectating). */
const EDGE_PAN_PX = 40;

/** Touch zoom target: CSS px of height that should show 14 world units at 32 px/unit. */
const TOUCH_VIEW_PX = 14 * 32;

export class Renderer {
  readonly camera = new Camera();

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: Ctx;
  private dpr = 1;
  /** World zoom (1 on desktop; < 1 on small touch screens). */
  private zoom = 1;
  private touchMode = false;
  private cssW = 1;
  private cssH = 1;
  private readonly ground = new Ground();
  private readonly particles = new Particles();
  private readonly numbers = new Numbers();
  private readonly effects = new Effects();
  /** Rain and Blizzard: drops and flakes, puddles and frost, a tint (weather.ts). */
  private readonly weather = new Weather();
  private readonly hud = new Hud();
  private readonly look = emptyLook();
  private readonly items: DrawItem[] = [];
  private itemCount = 0;
  private readonly firstSeen = new Map<number, number>();
  private readonly armored = new Map<number, boolean>();
  /** Feel state per entity: when an enemy was first drawn (spawn squash), its last act (boss stomp), a wizard's lean. */
  private readonly enemyFirstSeen = new Map<number, number>();
  private readonly enemyAct = new Map<number, string>();
  private readonly leans = new Map<number, number>();
  private readonly nudge: Vec2 = { x: 0, y: 0 };
  private readonly pose: Pose = { sx: 1, sy: 1, alpha: 1 };
  /** Seconds left of a held frame (hit-stop), and when the last one started. */
  private hitStop = 0;
  private lastStopAt = -1;
  private readonly cursorW: Vec2 = { x: ARENA.w / 2, y: ARENA.h / 2 };
  private cursorPx = -1;
  private cursorPy = -1;
  private readonly keys = new Set<string>();
  private shift = false;
  private time = 0;
  private fpsEma = 60;
  private lastSweep = 0;
  private reduceMotion = false;
  private centredOnce = false;
  /** The map being drawn (the room's level, with its doors and ice as they stand). */
  private arena: Arena = ARENA;
  /** Renderer time each door was seen opening (it sinks into the floor over DOOR_SINK_S). */
  private readonly doorOpenedAt = new Map<string, number>();
  /** Enemy sets the current level's story will need (spriteSets.ts levelArt), loaded ahead from the lobby. */
  private aheadArt: string[] = [];
  /** Each wizard's bubble as last drawn, so one that just ran out pops. */
  private readonly bubbles = new Map<number, number>();
  /** The ViewModel.epoch last drawn. */
  private epoch = 0;
  private readonly onHazardGone = (h: HazardSeen): void => { this.effects.onHazardGone(h); };

  private readonly onPointerMove = (e: PointerEvent): void => {
    const r = this.canvas.getBoundingClientRect();
    this.cursorPx = e.clientX - r.left;
    this.cursorPy = e.clientY - r.top;
  };
  private readonly onPointerLeave = (): void => { this.cursorPx = -1; this.cursorPy = -1; };
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.code.startsWith('Arrow')) this.keys.add(e.code);
    if (e.key === 'Shift') this.shift = true;
  };
  private readonly onKeyUp = (e: KeyboardEvent): void => {
    if (e.code.startsWith('Arrow')) this.keys.delete(e.code);
    if (e.key === 'Shift') this.shift = false;
  };
  private readonly onBlur = (): void => { this.keys.clear(); this.shift = false; };
  private readonly onResize = (): void => { this.resize(); };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerleave', this.onPointerLeave);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('resize', this.onResize);
    this.weather.setArena(this.arena);
    this.resize();
    // The sprite sets land in the background; until then (and without them) the vector bodies draw.
    // Only the wizard, the early waves' cast and the spell effects load now; the rest wait for the waves that bring them.
    void art.init(ART_MANIFEST_URL, { eager: [...EARLY_ART, ...FX_ART] });
  }

  /** Size the backing store to the canvas' CSS box at the device pixel ratio. */
  resize(): void {
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    const w = Math.max(1, Math.round(this.canvas.clientWidth || window.innerWidth));
    const h = Math.max(1, Math.round(this.canvas.clientHeight || window.innerHeight));
    this.dpr = dpr;
    this.cssW = w;
    this.cssH = h;
    const bw = Math.round(w * dpr), bh = Math.round(h * dpr);
    if (this.canvas.width !== bw) this.canvas.width = bw;
    if (this.canvas.height !== bh) this.canvas.height = bh;
    // Small touch screens zoom the world out (HUD stays full size) so a sideways phone sees ~14 units of height.
    this.zoom = this.touchMode ? Math.max(0.55, Math.min(1, h / TOUCH_VIEW_PX)) : 1;
    this.camera.resize(w / this.zoom, h / this.zoom);
    this.ground.ensure(dpr);
    art.setDetail(this.camera.scale * this.zoom * dpr);
  }

  /** Touch devices: zoomed-out world and no mouse cursor drawing. */
  setTouchMode(on: boolean): void {
    if (this.touchMode === on) return;
    this.touchMode = on;
    this.hud.touch = on;
    this.weather.touch = on;
    this.particles.budget = on ? TOUCH_PARTICLE_BUDGET : 1;
    this.effects.shots.limit = on ? TOUCH_SHOTS : FX_SHOTS_MAX;
    setSprayBudget(on);
    this.resize();
  }

  /** CSS px in the canvas -> world units, honouring the touch zoom. */
  screenToWorld(px: number, py: number): Vec2 {
    return this.camera.screenToWorld(px / this.zoom, py / this.zoom);
  }

  /** The glyph trail's hint when CSS px in the canvas are over the arena wall's glyph, else null. */
  trailHintAt(px: number, py: number): string | null {
    const w = this.screenToWorld(px, py);
    return trailGlyphHint(this.arena, w.x, w.y);
  }

  setReduceMotion(b: boolean): void {
    this.reduceMotion = b;
    this.camera.reduceMotion = b;
    this.weather.reduceMotion = b;
  }

  /** Draw another map (the room's level changed): camera bounds, floor and pillars follow it. */
  setArena(arena: Arena): void {
    if (arena === this.arena) return;
    // the same level with a door opened or water frozen: only the walls changed
    const sameLevel = arena.id === this.arena.id;
    this.arena = arena;
    // the minimap follows the walls as they change (ice bridges)
    this.hud.setArena(arena);
    if (sameLevel) return;
    // a story level: what its rooms will send loads from the lobby on (draw), so nothing pops in mid-act
    this.aheadArt = levelArt(levelById(arena.id));
    this.weather.setArena(arena);
    this.doorOpenedAt.clear();
    // a new map: the old one's walls and mines are simply gone, not broken
    resetHazards();
    this.camera.setBounds(arena.w, arena.h);
    this.ground.setArena(arena);
    this.centredOnce = false;
  }

  /** Override the cursor (CSS px in the canvas) when Controls owns pointer tracking. */
  setCursor(px: number, py: number): void {
    this.cursorPx = px;
    this.cursorPy = py;
  }

  /** Cursor in world units as of the last frame. */
  get cursorWorld(): Vec2 { return this.cursorW; }

  /** Free-camera pan in world units (spectating). */
  pan(dx: number, dy: number): void { this.camera.pan(dx, dy); }

  dispose(): void {
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerleave', this.onPointerLeave);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('resize', this.onResize);
  }

  // ---------------------------------------------------------------- frame

  draw(vm: ViewModel, dtSeconds: number): void {
    if (vm.epoch !== this.epoch) {
      this.epoch = vm.epoch;
      this.startOver();
    }
    const dt = Math.min(0.1, Math.max(0, dtSeconds));
    this.time += dt;
    if (dt > 0) this.fpsEma += (1 / dt - this.fpsEma) * 0.05;
    const ctx = this.ctx;
    const cam = this.camera;
    const w = this.cssW, h = this.cssH;
    const localPid = vm.me ? vm.me.pid : -1;
    const local = findLocal(vm, localPid);
    const dead = vm.me ? vm.me.dead : false;
    // The late enemies' sets come in once the match gets near them (any one drawn earlier asks for itself).
    if (vm.wave && vm.wave.n >= LATE_ART_FROM_WAVE) art.preload();
    // A story level's enemies, from the lobby on (cheap every frame; it also catches a manifest that lands late).
    if (this.aheadArt.length > 0) art.preload(this.aheadArt);

    // --- events land first, so a held frame (hit-stop) misses none of them
    if (vm.events.length > 0) {
      this.effects.onEvents(vm.events, vm, localPid, { particles: this.particles, numbers: this.numbers, camera: cam });
      this.hud.onEvents(vm.events, vm, localPid);
      for (const ev of vm.events) {
        if (ev.e === 'door') {
          if (ev.open) this.doorOpenedAt.set(ev.id, this.time);
          else this.doorOpenedAt.delete(ev.id);
          this.particles.dust(ev.x, ev.y, 18, 1.4);
          cam.shake(FEEL.shake.maxPx * 0.4, 0.5);
        }
      }
    }
    const stop = this.effects.takeStop();
    if (stop > 0 && !this.reduceMotion && this.time - this.lastStopAt > FEEL.hit.stopGapS) {
      this.hitStop = stop;
      this.lastStopAt = this.time;
    }
    // Hit-stop: the last frame stays on screen and nothing advances.
    if (this.hitStop > 0) {
      this.hitStop -= dt;
      return;
    }

    // --- camera
    this.updateCamera(local, dead, dt);
    cam.update(dt);
    const hasCursor = this.cursorPx >= 0;
    if (hasCursor) {
      const vw = w / this.zoom, vh = h / this.zoom;
      this.cursorW.x = (this.cursorPx / this.zoom - vw * 0.5 - cam.shakeX) / cam.scale + cam.x;
      this.cursorW.y = (this.cursorPy / this.zoom - vh * 0.5 - cam.shakeY) / cam.scale + cam.y;
    }
    // Shift implies Area; everything else previews Force (Self / Weapon are instant presses).
    this.hud.heldMode = this.shift ? 'area' : 'force';

    // --- simulation of the visual state
    this.trackTransitions(vm);
    trackHazards(vm.hazards, this.time, this.onHazardGone);
    this.particles.step(dt);
    this.numbers.step(dt);
    this.effects.step(dt);
    sprayPuffs.step(dt);
    // The Great Flood rains like Rain and Eternal Winter snows like a Blizzard.
    const rainLeft = vm.wave ? Math.max(vm.wave.rain, vm.wave.fl ?? 0) : 0;
    const snowLeft = vm.wave ? Math.max(vm.wave.blizzard, vm.wave.ew ?? 0) : 0;
    this.weather.step(dt, rainLeft, snowLeft, cam);
    this.hud.step(dt, vm);

    // --- layer 1: ground
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = GROUND.void;
    ctx.fillRect(0, 0, w, h);
    const wz = this.dpr * this.zoom;
    ctx.setTransform(wz, 0, 0, wz, 0, 0);
    this.ground.draw(ctx, cam.sx(0), cam.sy(0), snowLeft, this.time);
    if (vm.terrain) {
      drawIce(ctx, cam, this.arena, vm.terrain.i, this.time);
      drawChasmFills(ctx, cam, this.arena, vm.terrain, this.time);
      drawFlood(ctx, cam, this.arena, vm.terrain, this.time);
    }
    drawBrambles(ctx, cam, this.arena, vm.terrain, this.time);
    this.weather.drawGround(ctx, cam);

    // --- layer 2: floor hazards, ground effects, aim ghost, body ground marks
    for (const hz of vm.hazards) drawHazardFloor(ctx, cam, hz, this.time, this.particles, dt);
    for (const m of vm.markers) drawMarkerFloor(ctx, cam, m, this.time, this.particles, dt);
    for (const e of vm.enemies) if (e.ph === 2) drawBossRage(ctx, cam, e, this.time, this.particles, dt);
    this.effects.drawGround(ctx, cam, this.time);
    if (vm.me && local && !dead && vm.me.queue.length > 0 && hasCursor) {
      drawAimGhost(ctx, cam, vm.me, local, this.hud.heldMode, this.cursorW, this.time);
    }
    // holding a rock: where it comes down if let go now
    if (vm.me?.charging && local && !dead) drawThrowPreview(ctx, cam, local, vm.me.charging, vm.me.aim ?? local.facing, this.arena, this.time);
    this.drawGroundMarks(vm);
    // capture the flag: team rings under the wizards, the flags on their plinths or where they fell
    for (const p of vm.players) drawTeamRing(ctx, cam, p, this.time);
    drawGroundFlags(ctx, cam, vm.ctf, this.time);

    // --- layer 3: corpses, and enemy bodies in their death squash
    for (const c of vm.corpses) drawCorpse(ctx, cam, c, vm.renderTime, this.time);
    this.drawDying();

    // --- layer 4: y-sorted bodies (+ wall chords), then labels
    this.collectItems(vm);
    this.drawItems(vm, dt);
    for (const m of vm.markers) drawMarkerSky(ctx, cam, m, this.time);
    drawDepthMarks(ctx, cam, vm, this.time);
    this.drawLabels(vm);

    // --- layer 6: channels (additive), hazard hp hints
    for (const b of vm.beams) { drawBeam(ctx, cam, b, this.time); emitBeamSparks(this.particles, b, dt); }
    for (const s of vm.sprays) drawSpray(ctx, cam, s, this.particles, dt, this.time);
    sprayPuffs.draw(ctx, cam);
    for (const a of vm.arcs) drawArc(ctx, cam, a, this.time);
    if (hasCursor) for (const hz of vm.hazards) drawHazardHpHint(ctx, cam, hz, this.cursorW, this.time);
    this.effects.drawAir(ctx, cam, this.time);

    // --- layers 7-8: particles, numbers
    this.particles.draw(ctx, cam);
    this.weather.drawAir(ctx, cam);
    this.numbers.draw(ctx, cam);

    // --- cursor, vignettes, HUD (screen space, never zoomed)
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    if (hasCursor && !dead && !this.touchMode) drawCursor(ctx, this.cursorPx, this.cursorPy, vm.me ? dominantOf(vm.me.queue) : null, this.shift, this.reduceMotion ? 0 : this.time);
    this.effects.drawScreen(ctx, w, h);
    this.weather.drawScreen(ctx, w, h);
    drawTimeStop(ctx, cam, vm, w, h, this.zoom, this.time);
    this.hud.draw(ctx, vm, w, h, this.time, vm.fps > 0 ? vm.fps : this.fpsEma);

    if (this.time - this.lastSweep > 1) {
      this.lastSweep = this.time;
      sweepHazardCache(this.time);
      sweepStormColumns(this.time);
      this.effects.sweep();
      sweepFrozen((id) => vm.enemies.some((e) => e.id === id) || vm.players.some((p) => p.id === id));
      this.bubbles.forEach((_, id) => { if (!vm.players.some((p) => p.id === id)) this.bubbles.delete(id); });
      this.firstSeen.forEach((_, id) => { if (!vm.projectiles.some((p) => p.id === id)) this.firstSeen.delete(id); });
      const gone = (id: number): boolean => !vm.enemies.some((e) => e.id === id);
      this.armored.forEach((_, id) => { if (gone(id)) this.armored.delete(id); });
      this.enemyFirstSeen.forEach((_, id) => { if (gone(id)) this.enemyFirstSeen.delete(id); });
      this.enemyAct.forEach((_, id) => { if (gone(id)) this.enemyAct.delete(id); });
      this.leans.forEach((_, id) => { if (!vm.players.some((p) => p.id === id)) this.leans.delete(id); });
    }
  }

  /** The picture starts over (ViewModel.epoch): the old room's walls and mines are gone, not broken, the Book stops talking, and its ids mean nothing now. */
  private startOver(): void {
    resetHazards();
    this.hud.captions.clear();
    this.doorOpenedAt.clear();
    this.bubbles.clear();
    this.firstSeen.clear();
    this.armored.clear();
    this.enemyFirstSeen.clear();
    this.enemyAct.clear();
    this.leans.clear();
  }

  /** Bodies that just died: squash flat and fade (FEEL.deathS) where they stood, under the live bodies. */
  private drawDying(): void {
    const ctx = this.ctx, cam = this.camera;
    for (const d of this.effects.dying) {
      // A set with a death clip plays it, held on its last frame; otherwise the vector body squashes.
      const set = art.set(enemySetName(d.e)) ?? art.set(d.e.k);
      if (set && set.has('death')) {
        set.draw(ctx, cam, d.e.id, 'death', d.e.facing, this.time, d.e.rx, d.e.ry, { at: d.t });
        continue;
      }
      const f = d.t / d.dur;
      deathPose(f, this.pose);
      const px = cam.sx(d.e.rx), py = cam.sy(d.e.ry);
      ctx.save();
      ctx.globalAlpha = this.pose.alpha;
      ctx.translate(px, py);
      ctx.scale(this.pose.sx, this.pose.sy);
      ctx.translate(-px, -py);
      statusLook(d.e.st, this.time, d.e.id, this.look);
      drawEnemy(ctx, cam, d.e, this.look, 0.6 * (1 - f), this.time);
      ctx.restore();
    }
  }

  /** A wizard tilts into sideways movement, eased so starts and stops read as weight rather than a snap. */
  private leanOf(p: ViewPlayer, dt: number): number {
    const target = Math.max(-1, Math.min(1, p.vx / PLAYER_SPEED)) * FEEL.lean.rad;
    const cur = this.leans.get(p.id) ?? 0;
    let next = cur + (target - cur) * easeK(FEEL.lean.rate, dt);
    if (Math.abs(next) < 1e-4) next = 0;
    this.leans.set(p.id, next);
    return next;
  }

  private updateCamera(local: ViewPlayer | null, dead: boolean, dt: number): void {
    const cam = this.camera;
    if (local && !dead) {
      cam.free = false;
      cam.follow({ x: local.rx, y: local.ry }, this.cursorPx >= 0 ? this.cursorW : { x: local.rx, y: local.ry }, dt);
      return;
    }
    if (dead) {
      cam.free = true;
      let dx = 0, dy = 0;
      if (this.keys.has('ArrowLeft')) dx -= 1;
      if (this.keys.has('ArrowRight')) dx += 1;
      if (this.keys.has('ArrowUp')) dy -= 1;
      if (this.keys.has('ArrowDown')) dy += 1;
      if (this.cursorPx >= 0) {
        if (this.cursorPx < EDGE_PAN_PX) dx -= 1;
        else if (this.cursorPx > this.cssW - EDGE_PAN_PX) dx += 1;
        if (this.cursorPy < EDGE_PAN_PX) dy -= 1;
        else if (this.cursorPy > this.cssH - EDGE_PAN_PX) dy += 1;
      }
      if (dx !== 0 || dy !== 0) {
        const l = Math.hypot(dx, dy);
        cam.pan((dx / l) * FREE_PAN_SPEED * dt, (dy / l) * FREE_PAN_SPEED * dt);
      }
      return;
    }
    // No local player yet (landing / lobby before the first snapshot): centre the arena.
    if (!this.centredOnce) {
      cam.jumpTo(this.arena.w / 2, this.arena.h / 2);
      this.centredOnce = true;
    }
  }

  /**
   * Orc armor breaking throws its plates off; projectiles and enemies remember when they were
   * first seen (height arc, spawn squash); the jotunn's swing lands on the ground too.
   */
  private trackTransitions(vm: ViewModel): void {
    for (const e of vm.enemies) {
      if (!this.enemyFirstSeen.has(e.id)) this.enemyFirstSeen.set(e.id, this.time);
      const prevAct = this.enemyAct.get(e.id);
      if (e.boss && e.a === 'attack' && prevAct !== undefined && prevAct !== 'attack') {
        this.camera.shake(FEEL.shake.bossStomp, FEEL.shake.bossStompS);
        this.particles.dust(e.rx, e.ry, 14, 2.5);
      }
      this.enemyAct.set(e.id, e.a);
      if (e.k !== 'orc') continue;
      const now = e.ar > 0;
      const prev = this.armored.get(e.id);
      if (prev === true && !now) this.particles.chunks(ENEMY_COLORS.orc.accent, e.rx, e.ry, 0.6, 12, 4);
      this.armored.set(e.id, now);
    }
    for (const p of vm.projectiles) if (!this.firstSeen.has(p.id)) this.firstSeen.set(p.id, this.time);
    // a bubble that ran out (or was knocked down) pops where its wizard stands
    for (const p of vm.players) {
      const was = this.bubbles.get(p.id) ?? 0;
      if (was > 0 && p.bub <= 0 && !p.dead) this.effects.shots.spawn('fx-status', 'bubble-pop', p.rx, p.ry);
      this.bubbles.set(p.id, p.bub);
    }
  }

  private drawGroundMarks(vm: ViewModel): void {
    const ctx = this.ctx, cam = this.camera;
    for (const p of vm.players) {
      if (p.dead) continue;
      statusLook(p.st, this.time, p.id, this.look);
      drawWizardGround(ctx, cam, p, this.look, this.time);
    }
    for (const e of vm.enemies) {
      statusLook(e.st, this.time, e.id, this.look);
      drawEnemyGround(ctx, cam, e, this.look, this.time);
    }
    for (const pr of vm.projectiles) {
      drawProjectileShadow(ctx, cam, pr, projectileHeight(pr, this.time - (this.firstSeen.get(pr.id) ?? this.time)));
    }
  }

  private item(y: number, kind: number, i: number, j: number): void {
    let it = this.items[this.itemCount];
    if (!it) { it = { y: 0, kind: 0, i: 0, j: 0 }; this.items.push(it); }
    it.y = y; it.kind = kind; it.i = i; it.j = j;
    this.itemCount++;
  }

  private collectItems(vm: ViewModel): void {
    this.itemCount = 0;
    const cam = this.camera;
    const top = cam.top - 3, bottom = cam.bottom + 1, left = cam.left - 2, right = cam.right + 2;
    for (let i = 0; i < vm.players.length; i++) {
      const p = vm.players[i]!;
      if (p.dead || p.ry < top || p.ry > bottom || p.rx < left || p.rx > right) continue;
      this.item(p.ry, ITEM_PLAYER, i, 0);
    }
    for (let i = 0; i < vm.enemies.length; i++) {
      const e = vm.enemies[i]!;
      if (e.ry < top || e.ry > bottom || e.rx < left || e.rx > right) continue;
      this.item(e.ry, ITEM_ENEMY, i, 0);
    }
    for (let i = 0; i < vm.projectiles.length; i++) {
      const p = vm.projectiles[i]!;
      if (p.ry < top || p.ry > bottom || p.rx < left || p.rx > right) continue;
      this.item(p.ry, ITEM_PROJ, i, 0);
    }
    for (let i = 0; i < this.arena.pillars.length; i++) {
      const p = this.arena.pillars[i]!;
      // a footing under a level object (the boiler) is drawn by the object
      if (p.art === 'footing' || p.y < top || p.y > bottom) continue;
      this.item(p.y + p.r * 0.4, ITEM_PILLAR, i, 0);
    }
    for (let i = 0; i < vm.markers.length; i++) {
      const m = vm.markers[i]!;
      if (!markerStandsUp(m) || m.ry < top - 4 || m.ry > bottom || m.rx < left - 4 || m.rx > right) continue;
      this.item(m.ry, ITEM_MARKER, i, 0);
    }
    // story maps: shut (or sinking) doors stand on their footing line, rune stones on their base
    for (let i = 0; i < this.arena.doors.length; i++) {
      const d = this.arena.doors[i]!;
      const foot = d.rect.y + d.rect.h;
      if (foot < top || d.rect.y > bottom + 2 || d.rect.x > right || d.rect.x + d.rect.w < left) continue;
      if (d.open && this.doorSink(d.id) >= 1) continue;
      this.item(foot, ITEM_DOOR, i, 0);
    }
    for (let i = 0; i < this.arena.objects.length; i++) {
      const o = this.arena.objects[i]!;
      if (o.k !== 'rune-stone' || o.y < top || o.y > bottom || o.x < left || o.x > right) continue;
      this.item(o.y, ITEM_STONE, i, 0);
    }
    for (let i = 0; i < vm.hazards.length; i++) {
      const hz = vm.hazards[i]!;
      if (hz.k !== 'barrier' && hz.k !== 'wall') continue;
      const segs = hazardSegments(hz, this.time);
      for (let j = 0; j < segs.length; j++) {
        const [a, b] = segs[j]!;
        const my = (a.y + b.y) * 0.5;
        if (my < top || my > bottom) continue;
        this.item(my, ITEM_SEG, i, j);
      }
    }
    // storm walls: their columns stand up along the line and sort with the bodies
    for (let i = 0; i < vm.hazards.length; i++) {
      const hz = vm.hazards[i]!;
      if (hz.k !== 'storm') continue;
      const cols = stormColumns(hz, hazardSegments(hz, this.time), this.time);
      for (let j = 0; j < cols.length; j++) {
        const c = cols[j]!;
        if (c.y < top || c.y > bottom + 2 || c.x < left || c.x > right) continue;
        this.item(c.y, ITEM_STORM, i, j);
      }
    }
    // Insertion sort: frame-to-frame order is nearly stable, so this is ~O(n).
    const items = this.items;
    for (let k = 1; k < this.itemCount; k++) {
      const cur = items[k]!;
      let m = k - 1;
      while (m >= 0 && items[m]!.y > cur.y) { items[m + 1] = items[m]!; m--; }
      items[m + 1] = cur;
    }
  }

  private drawItems(vm: ViewModel, dt: number): void {
    const ctx = this.ctx, cam = this.camera;
    for (let k = 0; k < this.itemCount; k++) {
      const it = this.items[k]!;
      switch (it.kind) {
        case ITEM_PLAYER: {
          const p = vm.players[it.i]!;
          statusLook(p.st, this.time, p.id, this.look);
          this.effects.nudgeOf(p.id, this.nudge);
          this.look.jx += this.nudge.x;
          this.look.jy += this.nudge.y;
          const lean = this.leanOf(p, dt);
          if (lean !== 0) {
            const px = cam.sx(p.rx), py = cam.sy(p.ry);
            ctx.save();
            ctx.translate(px, py);
            ctx.rotate(lean);
            ctx.translate(-px, -py);
          }
          drawWizard(ctx, cam, p, this.look, this.effects.flashOf(p.id), this.time);
          if (lean !== 0) ctx.restore();
          drawCarriedFlag(ctx, cam, p, this.time);
          emitStatusParticles(this.particles, p.rx, p.ry, p.facing, this.look, dt);
          break;
        }
        case ITEM_ENEMY: {
          const e = vm.enemies[it.i]!;
          statusLook(e.st, this.time, e.id, this.look);
          this.effects.nudgeOf(e.id, this.nudge);
          this.look.jx += this.nudge.x;
          this.look.jy += this.nudge.y;
          const squash = spawnPose(this.time - (this.enemyFirstSeen.get(e.id) ?? this.time), this.pose);
          if (squash) {
            const px = cam.sx(e.rx), py = cam.sy(e.ry);
            ctx.save();
            ctx.translate(px, py);
            ctx.scale(this.pose.sx, this.pose.sy);
            ctx.translate(-px, -py);
          }
          drawEnemy(ctx, cam, e, this.look, this.effects.flashOf(e.id), this.time);
          if (squash) ctx.restore();
          emitStatusParticles(this.particles, e.rx, e.ry, e.facing, this.look, dt);
          break;
        }
        case ITEM_PROJ: {
          const pr = vm.projectiles[it.i]!;
          const age = this.time - (this.firstSeen.get(pr.id) ?? this.time);
          drawProjectile(ctx, cam, pr, projectileHeight(pr, age), this.time);
          // a dust mote now and then under heavy projectiles (about 12/s); client-side randomness is fine here
          if ((pr.k === 'boulder' || pr.k === 'bomb') && Math.random() < dt * 12) this.particles.dust(pr.rx, pr.ry, 1, 0.5);
          break;
        }
        case ITEM_PILLAR:
          drawPillar(ctx, cam, this.arena.pillars[it.i]!, it.i, this.time);
          break;
        case ITEM_SEG: {
          const hz = vm.hazards[it.i]!;
          const seg = hazardSegments(hz, this.time)[it.j];
          if (seg) drawHazardSegment(ctx, cam, hz, seg, this.time, it.j);
          break;
        }
        case ITEM_STORM: {
          const hz = vm.hazards[it.i]!;
          const c = stormColumns(hz, hazardSegments(hz, this.time), this.time)[it.j];
          if (c) drawStormColumn(ctx, cam, hz, it.j, c.x, c.y, this.time);
          break;
        }
        case ITEM_MARKER: {
          const m = vm.markers[it.i];
          if (m) drawMarkerBody(ctx, cam, m, this.time, this.particles, dt);
          break;
        }
        case ITEM_DOOR: {
          const d = this.arena.doors[it.i];
          if (d) drawDoor(ctx, cam, d, d.open ? this.doorSink(d.id) : 0, this.time);
          break;
        }
        case ITEM_STONE: {
          const o = this.arena.objects[it.i];
          if (o && o.k === 'rune-stone') drawRuneStone(ctx, cam, o, stoneActive(vm.story, o.id), this.time);
          break;
        }
      }
    }
  }

  /** How far an opened door has sunk (0..1); a door seen open on arrival is already gone. */
  private doorSink(id: string): number {
    const at = this.doorOpenedAt.get(id);
    return at === undefined ? 1 : Math.min(1, (this.time - at) / DOOR_SINK_S);
  }

  private drawLabels(vm: ViewModel): void {
    const ctx = this.ctx, cam = this.camera;
    const maxHp = vm.me ? vm.me.maxHp : 1000;
    for (let i = 0; i < vm.players.length; i++) {
      const p = vm.players[i]!;
      if (p.dead) continue;
      drawWizardLabels(ctx, cam, p, i + 1, maxHp);
    }
    for (const e of vm.enemies) drawEnemyLabels(ctx, cam, e);
  }
}

function findLocal(vm: ViewModel, pid: number): ViewPlayer | null {
  for (const p of vm.players) if (p.isLocal || p.id === pid) return p;
  return null;
}
